import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TransactionDetails from './components/TransactionDetails';
import SubscriptionActivation from './components/SubscriptionActivation';
import ReceiptDownload from './components/ReceiptDownload';
import SupportSection from './components/SupportSection';

const PaymentConfirmationReceipt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [transaction, setTransaction] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const transactionFromState = location?.state?.transaction;
    if (transactionFromState) {
      setTransaction(transactionFromState);
    } else {
      // Mock transaction data if none provided
      setTransaction({
        id: 'TXN1234567890',
        amount: 149,
        plan: {
          id: 'professional',
          name: 'Professional',
          features: [
            'Unlimited Job Postings',
            'Enhanced Candidate Visibility',
            'Advanced Analytics Dashboard',
            'Priority Listing',
            'Email Support (24h response)'
          ]
        },
        billingCycle: 'monthly',
        paymentMethod: 'card',
        timestamp: new Date()?.toISOString(),
        discount: 0
      });
    }

    // Hide confetti after 3 seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, [location]);

  if (!transaction) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading confirmation...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Success Header */}
        <div className="bg-gradient-to-br from-success/10 via-primary/5 to-accent/5 border-b border-border mb-8 -mx-6 -mt-8 px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Success Animation */}
            <div className="mb-6 relative">
              <div className="w-24 h-24 mx-auto bg-success rounded-full flex items-center justify-center animate-scale-in shadow-warm-lg">
                <Icon name="Check" size={48} className="text-white" />
              </div>
              {showConfetti && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {[...Array(20)]?.map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 bg-accent rounded-full animate-confetti"
                      style={{
                        left: `${50 + (Math.random() - 0.5) * 100}%`,
                        animationDelay: `${Math.random() * 0.5}s`,
                        animationDuration: `${1 + Math.random()}s`
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Payment Successful!
            </h1>
            <p className="text-lg text-muted-foreground mb-2">
              Thank you for subscribing to {transaction?.plan?.name} Plan
            </p>
            <div className="inline-flex items-center gap-2 bg-card rounded-full px-4 py-2 border border-border shadow-warm-sm">
              <Icon name="Hash" size={16} className="text-muted-foreground" />
              <span className="text-sm font-mono text-foreground">{transaction?.id}</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Transaction Details & Receipt */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TransactionDetails transaction={transaction} />
            <ReceiptDownload transaction={transaction} />
          </div>

          {/* Subscription Activation */}
          <SubscriptionActivation plan={transaction?.plan} billingCycle={transaction?.billingCycle} />

          {/* Action Buttons */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-warm-sm">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                fullWidth
                iconName="Sparkles"
                onClick={() => navigate('/role-based-dashboard')}
              >
                Access Premium Features
              </Button>
              <Button
                variant="outline"
                size="lg"
                fullWidth
                iconName="LayoutDashboard"
                onClick={() => navigate('/role-based-dashboard')}
              >
                Return to Dashboard
              </Button>
            </div>
          </div>

          {/* Support Section */}
          <SupportSection />
        </div>
      </div>
      <style jsx>{`
        @keyframes scale-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(200px) rotate(360deg);
            opacity: 0;
          }
        }

        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }

        .animate-confetti {
          animation: confetti 1.5s ease-out forwards;
        }
      `}</style>
    </MainLayout>
  );
};

export default PaymentConfirmationReceipt;